import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { resolveImg } from "@/hooks/useSiteImages";
import { ImageField } from "./ImageField";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("nyla_admin_token")}` });

// Recommended asset specs per slot — keep the team aligned on upload sizes.
const IMAGE_SPECS = {
  hero_poster: {
    size: "2400 × 1200 px",
    ratio: "2 : 1",
    format: "JPG or WebP, ≤ 400 KB",
    note: "Fallback still shown before/without the hero video. Keep the subject centered — top & bottom crop on mobile.",
  },
  hero_video: {
    size: "1920 × 1080 (Full HD)",
    ratio: "16 : 9",
    format: "MP4 (H.264), 6–15 s loop, ≤ 8 MB, muted",
    note: "Autoplays silently on load. Design so it loops seamlessly — no hard cuts.",
  },
  product_alkaline: {
    size: "1600 × 2000 px",
    ratio: "4 : 5 (portrait)",
    format: "PNG with transparent background preferred",
    note: "Bottle centered with ~10% safe margin. Cards hover-zoom slightly.",
  },
  product_balanced: {
    size: "1600 × 2000 px",
    ratio: "4 : 5 (portrait)",
    format: "PNG with transparent background preferred",
    note: "Bottle centered with ~10% safe margin. Cards hover-zoom slightly.",
  },
  product_sparkling: {
    size: "1600 × 2000 px",
    ratio: "4 : 5 (portrait)",
    format: "PNG with transparent background preferred",
    note: "Bottle centered with ~10% safe margin. Sparkling variant shown in the third product card.",
  },
  sustainability: {
    size: "1600 × 1200 px",
    ratio: "4 : 3",
    format: "JPG or WebP, ≤ 400 KB",
    note: "Nature/water imagery. Sits beside three sustainability cards on desktop.",
  },
  vision_kingfisher: {
    size: "2400 × 1040 px",
    ratio: "≈ 2.3 : 1 (wide banner)",
    format: "JPG or WebP, ≤ 500 KB",
    note: "Full-width banner. Keep the kingfisher above center — a caption pill overlays the bottom.",
  },
};

export const ImagesManager = ({ onUnauthorized }) => {
  const [images, setImages] = useState(null);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/site-images`)
      .then((r) => setImages(r.data))
      .catch(() => toast.error("Could not load site images"));
  }, []);

  const save = async (key) => {
    const url = edits[key];
    if (!url) return;
    setSaving(key);
    try {
      await axios.put(`${API}/admin/site-images`, { key, url }, { headers: auth() });
      setImages({ ...images, [key]: { ...images[key], url } });
      setEdits({ ...edits, [key]: undefined });
      toast.success("Image updated — live on the site now");
    } catch (e) {
      if (e.response?.status === 401) onUnauthorized();
      else toast.error("Failed to save");
    } finally {
      setSaving("");
    }
  };

  if (!images) return <p className="text-sm text-[#86868B]">Loading…</p>;

  return (
    <div data-testid="images-manager">
      <h1 className="font-display text-3xl font-bold tracking-tight mb-2">Site Images</h1>
      <p className="text-sm text-[#86868B] mb-8">
        Change any image on the website — paste a URL or upload a file, then Save. Changes go live instantly.
      </p>
      <div className="grid md:grid-cols-2 gap-6">
        {Object.values(images).map((img) => {
          const current = edits[img.key] ?? img.url;
          const dirty = edits[img.key] !== undefined && edits[img.key] !== img.url;
          const isVideo = img.key === "hero_video";
          const spec = IMAGE_SPECS[img.key];
          return (
            <div key={img.key} data-testid={`image-slot-${img.key}`} className="bg-white rounded-3xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <p className="text-sm font-semibold mb-3">{img.label}</p>
              {!isVideo && (
                <img src={resolveImg(current)} alt={img.label} className="w-full h-40 object-cover rounded-2xl mb-4 bg-[#F5F5F7]" onError={(e) => (e.target.style.opacity = 0.2)} />
              )}
              {spec && (
                <div data-testid={`image-spec-${img.key}`} className="mb-4 rounded-2xl bg-[#F5F5F7] border border-[#E5E5EA] px-4 py-3">
                  <p className="text-[10px] font-semibold tracking-[0.15em] uppercase text-[#2C3E35] mb-1.5">Recommended Upload</p>
                  <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[12px] text-[#1D1D1F]">
                    <dt className="text-[#86868B]">Size</dt><dd className="font-medium">{spec.size}</dd>
                    <dt className="text-[#86868B]">Aspect</dt><dd className="font-medium">{spec.ratio}</dd>
                    <dt className="text-[#86868B]">Format</dt><dd className="font-medium">{spec.format}</dd>
                  </dl>
                  <p className="mt-2 text-[11px] leading-relaxed text-[#86868B]">{spec.note}</p>
                </div>
              )}
              <ImageField value={current} onChange={(v) => setEdits({ ...edits, [img.key]: v })} testId={`image-url-${img.key}`} kind={isVideo ? "video" : "image"} />
              <button
                data-testid={`image-save-${img.key}`}
                onClick={() => save(img.key)}
                disabled={!dirty || saving === img.key}
                className="mt-3 px-5 py-2 rounded-full bg-[#1D1D1F] text-white text-xs font-medium disabled:opacity-40 hover:bg-black"
              >
                {saving === img.key ? "Saving…" : "Save"}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};
